import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Sun, Moon, Star, Sparkles, Loader2, RefreshCw, 
  Heart, Gem, Leaf, Calendar
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import ReactMarkdown from 'react-markdown';

const DailyHoroscope = () => {
  const { token } = useAuth();
  const [horoscope, setHoroscope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchHoroscope();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHoroscope = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await axios.get(`${API}/horoscope/daily`, { headers, withCredentials: true });
      setHoroscope(res.data);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
        <p className="text-mystic-200/70">Reading the stars for you...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-horoscope-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Star className="w-8 h-8 text-moon-gold" />
              Daily Horoscope
            </h1>
            <p className="text-mystic-200/70">
              {horoscope?.date} • Personalized for your chart & cycle
            </p>
          </div>
          <Button
            onClick={() => fetchHoroscope(true)}
            disabled={refreshing}
            variant="outline"
            className="border-mystic-500/50 text-mystic-300 hover:bg-mystic-500/20"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </motion.div>

      {/* Context Cards */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        {/* Birth Chart */}
        <Card className="glass bg-gradient-to-br from-amber-500/10 to-transparent">
          <CardContent className="py-4">
            <div className="flex items-center gap-3 mb-3">
              <Sun className="w-5 h-5 text-amber-400" />
              <span className="text-mystic-300 font-medium">Your Signs</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-mystic-200/70 text-sm">Sun</span>
                <Badge className="bg-amber-500/20 text-amber-300">{horoscope?.birth_chart?.sun || "Unknown"}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-mystic-200/70 text-sm">Moon</span>
                <Badge className="bg-indigo-500/20 text-indigo-300">{horoscope?.birth_chart?.moon || "Unknown"}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-mystic-200/70 text-sm">Rising</span>
                <Badge className="bg-pink-500/20 text-pink-300">{horoscope?.birth_chart?.rising || "Unknown"}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Moon */}
        <Card className="glass bg-gradient-to-br from-indigo-500/10 to-transparent">
          <CardContent className="py-4">
            <div className="flex items-center gap-3 mb-3">
              <Moon className="w-5 h-5 text-indigo-400" />
              <span className="text-mystic-300 font-medium">Today's Moon</span>
            </div>
            <div className="text-center">
              <p className="text-3xl mb-1">{horoscope?.current_moon?.emoji}</p>
              <p className="text-white font-semibold">{horoscope?.current_moon?.phase}</p>
              <p className="text-mystic-200/70 text-sm">in {horoscope?.current_moon?.sign}</p>
            </div>
          </CardContent>
        </Card>

        {/* Cycle Phase */}
        <Card className="glass bg-gradient-to-br from-pink-500/10 to-transparent">
          <CardContent className="py-4">
            <div className="flex items-center gap-3 mb-3">
              <Heart className="w-5 h-5 text-pink-400" />
              <span className="text-mystic-300 font-medium">Cycle Phase</span>
            </div>
            <div className="text-center">
              <p className="text-white font-semibold capitalize">{horoscope?.cycle?.phase}</p>
              <p className="text-mystic-200/70 text-sm">Day {horoscope?.cycle?.day}</p>
              <Badge variant="outline" className="mt-2 border-pink-500/30 text-pink-300 capitalize">
                {horoscope?.cycle?.energy} energy
              </Badge>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Horoscope Content */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="glass">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-moon-gold" />
              Your Cosmic Guidance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="text-xl font-bold text-white mt-4 mb-2">{children}</h1>,
                  h2: ({children}) => <h2 className="text-lg font-semibold text-mystic-300 mt-4 mb-2">{children}</h2>,
                  h3: ({children}) => <h3 className="text-base font-medium text-mystic-300 mt-3 mb-1">{children}</h3>,
                  p: ({children}) => <p className="text-mystic-100 mb-3 leading-relaxed">{children}</p>,
                  strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                  ul: ({children}) => <ul className="list-disc list-inside text-mystic-100 mb-3 space-y-1">{children}</ul>,
                  li: ({children}) => <li className="text-mystic-200/90">{children}</li>,
                }}
              >
                {horoscope?.horoscope}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* No Birth Chart Notice */}
      {horoscope?.birth_chart?.sun === "Unknown" && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass border-amber-500/30">
            <CardContent className="py-4 flex items-center gap-4">
              <Star className="w-8 h-8 text-amber-400 flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Complete Your Birth Chart</p>
                <p className="text-mystic-200/70 text-sm">
                  Add your birth details in the Astrology section for more personalized horoscope readings.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default DailyHoroscope;
